import { useState } from 'react'
import { Pencil, Check, X, Lightbulb } from 'lucide-react'
import {
  type PricingConfig,
  type PricingConfigUpdate,
  usePricingConfig,
  useUpdatePricingConfig,
} from '../../api/admin-stripe'

/**
 * Inline-editable view of the singleton pricing_config row.
 *
 * Mirrors the PolicyGroup pattern from AdminBillingOverviewTab — read-only
 * chips with a Pencil that swaps in an inline form. No modal lib.
 *
 * Saves are PATCH-style: only fields that changed are sent. The backend
 * round-trips the whole row so the displayed effective formula updates
 * with the operator's edit.
 */
export function PricingConfigPanel() {
  const cfg = usePricingConfig()
  const update = useUpdatePricingConfig()
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState<PricingConfigUpdate>({})

  function startEdit() {
    if (!cfg.data) return
    setDraft({})
    setEditing(true)
  }

  async function save(e: React.FormEvent) {
    e.preventDefault()
    // Only POST fields the operator actually touched.
    if (Object.keys(draft).length === 0) {
      setEditing(false)
      return
    }
    try {
      await update.mutateAsync(draft)
      setEditing(false)
      setDraft({})
    } catch {
      // Mutation error surfaces in update.error; we leave the form open.
    }
  }

  function field<K extends keyof PricingConfigUpdate>(
    key: K,
    value: PricingConfigUpdate[K],
  ) {
    setDraft((prev) => ({ ...prev, [key]: value }))
  }

  if (cfg.isLoading) {
    return (
      <div className="rounded-lg border border-border bg-surface p-4 text-sm text-text-secondary">
        Loading pricing config…
      </div>
    )
  }

  if (!cfg.data) {
    return (
      <div className="rounded-lg border border-border bg-surface p-4 text-sm text-error">
        Could not load pricing config.{' '}
        <button onClick={() => cfg.refetch()} className="underline">Retry</button>
      </div>
    )
  }

  const c = cfg.data
  const draftFor = <K extends keyof PricingConfigUpdate>(key: K, fallback: PricingConfig[K]): PricingConfigUpdate[K] | PricingConfig[K] => {
    return draft[key] ?? fallback
  }

  return (
    <section className="overflow-hidden rounded-lg border border-border bg-surface">
      <header className="flex items-center justify-between border-b border-border px-4 py-3">
        <div className="font-medium">Pricing config</div>
        {!editing ? (
          <button
            onClick={startEdit}
            className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
          >
            <Pencil className="h-3 w-3" /> Edit
          </button>
        ) : (
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => { setEditing(false); setDraft({}) }}
              className="rounded border border-border p-1.5 hover:bg-surface-elevated"
              title="Discard"
            >
              <X className="h-3.5 w-3.5" />
            </button>
            <button
              type="submit"
              form="pricing-config-form"
              disabled={update.isPending}
              className="rounded bg-primary p-1.5 text-white hover:opacity-90 disabled:opacity-50"
              title="Save (does not mutate Stripe — drives next attach + next reporter pass)"
            >
              <Check className="h-3.5 w-3.5" />
            </button>
          </div>
        )}
      </header>

      <div className="space-y-3 p-4">
        <div className="rounded border border-primary/30 bg-primary/5 p-3 text-sm">
          <div className="flex items-start gap-2 text-text-secondary">
            <Lightbulb className="mt-0.5 h-4 w-4 flex-shrink-0 text-primary" />
            <div>
              <div className="font-medium text-text-primary">Effective formula</div>
              <div className="mt-0.5 font-mono text-xs">
                {effectiveFormulaPreview(c, draft)}
              </div>
              <div className="mt-1 text-xs">
                Changes apply to subsequent meter posts and new subscription attaches only.
                Past usage rows and closed Stripe invoices are not rewritten.
              </div>
            </div>
          </div>
        </div>

        <form id="pricing-config-form" onSubmit={save} className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <NumField
            label="Minutes price (USD per minute)"
            value={Number(draftFor('minutes_price_usd_per_minute', c.minutes_price_usd_per_minute))}
            editing={editing}
            min={0}
            step={0.01}
            prefix="$"
            onChange={(v) => field('minutes_price_usd_per_minute', v)}
          />
          <NumField
            label="Free GB (no charge below this)"
            value={Number(draftFor('storage_free_gb', c.storage_free_gb))}
            editing={editing}
            min={0}
            step={0.5}
            onChange={(v) => field('storage_free_gb', v)}
          />
          <NumField
            label="Block size (GB per pricing unit)"
            value={Number(draftFor('storage_block_gb', c.storage_block_gb))}
            editing={editing}
            min={0.1}
            step={0.5}
            onChange={(v) => field('storage_block_gb', v)}
          />
          <NumField
            label="Price per block (USD)"
            value={Number(draftFor('storage_price_usd_per_block', c.storage_price_usd_per_block))}
            editing={editing}
            min={0}
            step={0.5}
            prefix="$"
            onChange={(v) => field('storage_price_usd_per_block', v)}
          />
          <NumField
            label="Storage cost (Railway/S3 wholesale, $/GB-month)"
            value={Number(draftFor('storage_cost_usd_per_gb_month', c.storage_cost_usd_per_gb_month))}
            editing={editing}
            min={0}
            step={0.005}
            prefix="$"
            onChange={(v) => field('storage_cost_usd_per_gb_month', v)}
          />
          <SelectField
            label="Strategy"
            value={String(draftFor('storage_strategy', c.storage_strategy))}
            editing={editing}
            options={[
              { value: 'flat_block', label: 'Flat block (default)' },
              { value: 'bundled_retention', label: 'Bundled retention (alternative)' },
            ]}
            onChange={(v) => field('storage_strategy', v as 'flat_block' | 'bundled_retention')}
          />
          <TextField
            label="Metered minutes Price ID"
            value={String(draftFor('metered_minutes_price_id', c.metered_minutes_price_id ?? ''))}
            editing={editing}
            placeholder="price_..."
            onChange={(v) => field('metered_minutes_price_id', v || null)}
          />
          <TextField
            label="Storage Price ID"
            value={String(draftFor('storage_price_id', c.storage_price_id ?? ''))}
            editing={editing}
            placeholder="price_..."
            onChange={(v) => field('storage_price_id', v || null)}
          />
          <TextField
            label="Storage meter event_name"
            value={String(draftFor('storage_meter_event_name', c.storage_meter_event_name))}
            editing={editing}
            placeholder="voysi_storage_gb"
            onChange={(v) => field('storage_meter_event_name', v)}
          />
          <BoolField
            label="Storage reporter enabled (DB switch)"
            value={Boolean(draftFor('storage_reporter_enabled', c.storage_reporter_enabled))}
            editing={editing}
            hint="Both this AND the env STRIPE_STORAGE_REPORTER_ENABLED must be true"
            onChange={(v) => field('storage_reporter_enabled', v)}
          />
        </form>

        <div>
          <div className="text-xs text-text-secondary">
            Active price IDs (attached to new orgs' subscriptions on creation)
          </div>
          <div className="mt-1 flex flex-wrap gap-1">
            {(c.active_price_ids ?? []).length === 0 ? (
              <span className="text-xs text-text-secondary">— none —</span>
            ) : (
              c.active_price_ids.map((pid) => (
                <code
                  key={pid}
                  className="rounded border border-border bg-background px-1.5 py-0.5 text-xs"
                >
                  {pid}
                </code>
              ))
            )}
          </div>
          <div className="mt-1 text-xs text-text-secondary">
            Edit these via the Inventory table below — toggle the &ldquo;Active for new orgs&rdquo; column.
          </div>
        </div>

        {update.isError && (
          <div className="rounded border border-error/30 bg-error/5 p-2 text-xs text-error">
            Save failed: {String(update.error)}
          </div>
        )}
        <div className="text-xs text-text-secondary">
          Last updated{' '}
          {c.updated_at
            ? new Date(c.updated_at * 1000).toLocaleString()
            : '—'}
          {c.updated_by_user_id ? ` by ${c.updated_by_user_id}` : ''}
        </div>
      </div>
    </section>
  )
}

function effectiveFormulaPreview(c: PricingConfig, draft: PricingConfigUpdate): string {
  // Recompute client-side so the operator sees the formula update before
  // hitting save. Covers minutes (config-driven, mirrors Stripe's flat Price)
  // and storage (cost + price sides). Mirrors the backend's
  // effective_formula_text helper.
  const minutes = (draft.minutes_price_usd_per_minute ?? c.minutes_price_usd_per_minute) || 0
  const free = (draft.storage_free_gb ?? c.storage_free_gb) || 0
  const block = (draft.storage_block_gb ?? c.storage_block_gb) || 0
  const price = (draft.storage_price_usd_per_block ?? c.storage_price_usd_per_block) || 0
  const cost = (draft.storage_cost_usd_per_gb_month ?? c.storage_cost_usd_per_gb_month) || 0
  const strategy = draft.storage_strategy ?? c.storage_strategy
  const minutesPart = `Charges $${minutes} per minute.`
  let storagePart: string
  if (strategy === 'bundled_retention') {
    storagePart = 'Storage is included in the per-minute rate (extended retention only).'
  } else if (free <= 0) {
    storagePart = `Storage: $${price} per ${block} GB stored (cost ~$${cost}/GB-mo wholesale).`
  } else {
    storagePart = `Storage: $${price} per additional ${block} GB after ${free} GB free (cost ~$${cost}/GB-mo wholesale).`
  }
  return `${minutesPart} ${storagePart}`
}

function NumField({
  label, value, editing, onChange, min, step, prefix,
}: {
  label: string
  value: number
  editing: boolean
  onChange: (v: number) => void
  min?: number
  step?: number
  prefix?: string
}) {
  return (
    <div>
      <div className="text-xs text-text-secondary">{label}</div>
      {editing ? (
        <input
          type="number"
          value={String(value)}
          min={min}
          step={step}
          onChange={(e) => onChange(Number(e.target.value))}
          className="mt-0.5 w-full rounded border border-border bg-background px-2 py-1 font-mono text-sm"
        />
      ) : (
        <div className="mt-0.5 font-mono text-sm">{prefix ?? ''}{value}</div>
      )}
    </div>
  )
}

function TextField({
  label, value, editing, onChange, placeholder,
}: {
  label: string
  value: string
  editing: boolean
  onChange: (v: string) => void
  placeholder?: string
}) {
  return (
    <div>
      <div className="text-xs text-text-secondary">{label}</div>
      {editing ? (
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className="mt-0.5 w-full rounded border border-border bg-background px-2 py-1 font-mono text-sm"
        />
      ) : (
        <code className="mt-0.5 block rounded border border-border bg-background px-1.5 py-1 text-xs">
          {value || '— not set —'}
        </code>
      )}
    </div>
  )
}

function SelectField({
  label, value, editing, options, onChange,
}: {
  label: string
  value: string
  editing: boolean
  options: { value: string; label: string }[]
  onChange: (v: string) => void
}) {
  return (
    <div>
      <div className="text-xs text-text-secondary">{label}</div>
      {editing ? (
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="mt-0.5 w-full rounded border border-border bg-background px-2 py-1 text-sm"
        >
          {options.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      ) : (
        <div className="mt-0.5 font-mono text-sm">
          {options.find((o) => o.value === value)?.label ?? value}
        </div>
      )}
    </div>
  )
}

function BoolField({
  label, value, editing, hint, onChange,
}: {
  label: string
  value: boolean
  editing: boolean
  hint?: string
  onChange: (v: boolean) => void
}) {
  return (
    <div>
      <div className="text-xs text-text-secondary">{label}</div>
      {editing ? (
        <label className="mt-0.5 inline-flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={value}
            onChange={(e) => onChange(e.target.checked)}
          />
          <span>{value ? 'Enabled' : 'Disabled'}</span>
        </label>
      ) : (
        <div
          className={`mt-0.5 inline-block rounded px-2 py-0.5 text-xs ${
            value ? 'bg-success/20 text-success' : 'bg-surface-elevated text-text-secondary'
          }`}
        >
          {value ? 'Enabled' : 'Disabled'}
        </div>
      )}
      {hint && <div className="mt-1 text-xs text-text-secondary">{hint}</div>}
    </div>
  )
}
